import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Heart, Users, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { DecorativeShape } from '../components/DecorativeShape';
import { PageTransition } from '../components/layout/PageTransition';
import { Animate, Stagger } from '../components/ui/Animate';
import { useLanguage } from '../context/LanguageContext';
export function Home() {
  const {
    t
  } = useLanguage();
  return <PageTransition data-id="element-731">
      {/* Hero Section */}
      <section className="relative pt-20 pb-32 overflow-hidden" data-id="element-732">
        <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[600px] h-[600px] translate-x-1/3 -translate-y-1/4" data-id="element-733" />
        <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-[400px] h-[400px] -translate-x-1/4 translate-y-1/4" delay={0.5} data-id="element-734" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-735">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-736">
            <Stagger staggerDelay={0.15} data-id="element-737">
              <div className="inline-block px-4 py-2 bg-teal/10 text-teal-dark rounded-full font-bold text-sm mb-6" data-id="element-738">
                {t('home.hero.badge')}
              </div>
              <h1 className="md:text-6xl lg:text-7xl font-heading font-extrabold text-charcoal mb-6 text-[56px]" data-id="element-739">
                {t('home.hero.titleStart')}
                <span className="text-coral" data-id="element-740">{'hugrekki'}</span>
                {t('home.hero.and')}
                <span className="text-teal" data-id="element-741">{'f\xE6rni'}</span>
              </h1>
              <p className="text-xl text-gray-600 mb-8 leading-relaxed max-w-lg" data-id="element-742">
                {t('home.hero.description')}
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-743">
                <Link to="/contact" data-id="element-744">
                  <Button size="lg" rightIcon={<ArrowRight size={20} data-id="element-746" />} data-id="element-745">
                    {t('home.hero.cta')}
                  </Button>
                </Link>
                <Link to="/services" data-id="element-747">
                  <Button variant="outline" size="lg" data-id="element-748">
                    {t('home.hero.ctaSecondary')}
                  </Button>
                </Link>
              </div>
            </Stagger>

            <Animate delay={0.2} className="relative hidden lg:block" data-id="element-749">
              <div className="relative w-full aspect-square max-w-md mx-auto" data-id="element-750">
                <div className="absolute inset-0 bg-coral rounded-[3rem] rotate-6 opacity-20" data-id="element-751" />
                <div className="absolute inset-0 bg-teal rounded-[3rem] -rotate-6 opacity-20" data-id="element-752" />
                <img src="https://cdn.magicpatterns.com/uploads/6bt3PcPytwUVW4EyeZ9z5f/Helgi-6_A_height_x30_copy.jpg" alt="Helgi - Organizational Coach" className="relative w-full h-full object-cover rounded-[3rem] shadow-2xl" loading="eager" fetchPriority="high" data-id="element-753" />
                <div className="absolute -bottom-8 -left-8 bg-white p-4 rounded-2xl shadow-xl flex items-center gap-3 animate-float-gentle" data-id="element-754">
                  <div className="bg-yellow-100 p-2 rounded-full text-yellow-600" data-id="element-755">
                    <Star size={24} fill="currentColor" data-id="element-756" />
                  </div>
                  <div data-id="element-757">
                    <p className="font-bold text-charcoal" data-id="element-758">
                      {t('home.badge.leaders')}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-759">
                      {t('home.badge.subtitle')}
                    </p>
                  </div>
                </div>
              </div>
            </Animate>
          </div>
        </div>
      </section>

      {/* Value Pillars */}
      <section className="py-24 bg-cream relative" data-id="element-760">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-761">
          <Animate className="text-center max-w-2xl mx-auto mb-16" data-id="element-762">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-763">
              {t('home.pillars.title')}
            </h2>
            <p className="text-gray-600 text-lg" data-id="element-764">
              {'Ranns\xF3knastuddur grunnur, hagn\xFDtar a\xF0fer\xF0ir, hreinskilni um hva\xF0 virkar'}
            </p>
          </Animate>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-765">
            <Animate delay={0} data-id="element-766">
              <Card className="h-full text-center p-8" hoverEffect data-id="element-767">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 bg-yellow-100 text-yellow-600" data-id="element-768">
                  <Zap size={32} data-id="element-769" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-770">
                  {t('home.pillars.energy.title')}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-771">
                  {t('home.pillars.energy.desc')}
                </p>
              </Card>
            </Animate>

            <Animate delay={0.15} data-id="element-772">
              <Card className="h-full text-center p-8" hoverEffect data-id="element-773">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 bg-coral/20 text-coral" data-id="element-774">
                  <Heart size={32} data-id="element-775" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-776">
                  {t('home.pillars.connection.title')}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-777">
                  {t('home.pillars.connection.desc')}
                </p>
              </Card>
            </Animate>

            <Animate delay={0.3} data-id="element-778">
              <Card className="h-full text-center p-8" hoverEffect data-id="element-779">
                <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-6 bg-teal/20 text-teal" data-id="element-780">
                  <Users size={32} data-id="element-781" />
                </div>
                <h3 className="text-xl font-bold mb-3" data-id="element-782">
                  {t('home.pillars.teams.title')}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-783">
                  {t('home.pillars.teams.desc')}
                </p>
              </Card>
            </Animate>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 relative overflow-hidden" data-id="element-784">
        <DecorativeShape type="square" color="bg-coral/5" className="top-20 left-0 w-64 h-64 -rotate-12" data-id="element-785" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-786">
          <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6" data-id="element-787">
            <div data-id="element-788">
              <span className="text-teal font-bold tracking-wider uppercase text-sm" data-id="element-789">
                {t('home.services.sectionLabel')}
              </span>
              <h2 className="text-4xl font-bold mt-2" data-id="element-790">
                {t('home.services.title')}
              </h2>
            </div>
            <Link to="/services" data-id="element-791">
              <Button variant="outline" rightIcon={<ArrowRight size={16} data-id="element-793" />} data-id="element-792">
                {t('home.services.viewAll')}
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-794">
            {(['firstTime', 'conflicts', 'facilitation'] as const).map((key, i) => <Animate key={key} delay={i * 0.1} data-id="element-795">
                  <Link to="/services" data-id="element-796">
                    <Card hoverEffect className="h-full group border-2 border-transparent hover:border-teal/30 transition-colors" data-id="element-797">
                      <div className="flex flex-wrap gap-2 mb-4" data-id="element-798">
                        {(t(`home.services.items.${key}.tags`) as string[]).map((tag: string) => <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-bold rounded-full" data-id="element-799">
                              {tag}
                            </span>)}
                      </div>
                      <h3 className="text-2xl font-bold mb-3 group-hover:text-teal transition-colors" data-id="element-800">
                        {t(`home.services.items.${key}.title`)}
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-801">
                        {t(`home.services.items.${key}.desc`)}
                      </p>
                      <div className="flex items-center text-teal font-bold text-sm" data-id="element-802">
                        {t('home.services.learnMore')}{' '}
                        <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-803" />
                      </div>
                    </Card>
                  </Link>
                </Animate>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-charcoal text-white relative overflow-hidden rounded-t-[3rem] mx-4 sm:mx-8 mb-8" data-id="element-804">
        <DecorativeShape type="donut" color="border-white/10" className="top-0 right-0 w-96 h-96 translate-x-1/3 -translate-y-1/3" data-id="element-805" />
        <DecorativeShape type="blob" color="bg-coral" className="bottom-0 left-0 w-64 h-64 -translate-x-1/3 translate-y-1/3 blur-3xl opacity-50" data-id="element-806" />
        <Animate className="max-w-4xl mx-auto px-4 text-center relative z-10" data-id="element-807">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" data-id="element-808">
            {t('home.cta.title')}
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto" data-id="element-809">
            {t('home.cta.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-810">
            <Link to="/contact" data-id="element-811">
              <Button variant="primary" size="lg" className="w-full sm:w-auto" data-id="element-812">
                {t('home.cta.bookCall')}
              </Button>
            </Link>
            <Link to="/about" data-id="element-813">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 w-full sm:w-auto" data-id="element-814">
                {t('home.cta.readAbout')}
              </Button>
            </Link>
          </div>
        </Animate>
      </section>
    </PageTransition>;
}